// Marketplace Unifiée - Pros + Particuliers
// Feed unique avec filtres: Tout | Boutiques Pro | Annonces Particuliers
// "Trouve ton produit en 3 clics" - Achète sans intermédiaire et sans commission

import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Star, ShoppingCart, Package, Wrench, Zap, Droplets, 
  Flame, Home, Layers, X, Truck, Shield, Tag, Building2, ArrowLeft, 
  SlidersHorizontal, ChevronRight, Heart, Menu, User, ClipboardList,
  RotateCcw, Settings, HelpCircle, LogOut, Gift, Percent, Bell,
  Paintbrush, HardHat, Ruler, Wind, TreeDeciduous, ShieldCheck, Hammer,
  ChevronDown, Users, Store, MessageSquare, Briefcase, Newspaper, LogIn,
  AlertTriangle, Info, Euro, Filter, Check, Loader2, Award, MapPin, Clock,
  RefreshCw, BadgeCheck, Eye, MessageCircle, Repeat, HandHeart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useBuyer } from '../../contexts/BuyerContext';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';
import UnifiedFilterSystem from '../../components/filters/UnifiedFilterSystem';
import { useDemoMode } from '../../components/demo/DemoModeProvider';
import MarketplaceCatalogueMenu from '../../components/marketplace/MarketplaceCatalogueMenu';
import { ProductSkeleton } from '../../components/ui/skeletons';

import { API_URL } from '../../config/api';

// ══════════════════════════════════════════════════════════════════════════════
// CONSTANTES ET MAPPINGS
// ══════════════════════════════════════════════════════════════════════════════

// Filtres Seller Type Pills
const SELLER_TYPE_FILTERS = [
  { id: 'all', label: 'Tout', icon: Layers },
  { id: 'pro', label: 'Boutiques Pro', icon: Store },
  { id: 'particulier', label: 'Particuliers', icon: User },
];

// Filtres Transaction Type
const TRANSACTION_TYPES = [
  { id: 'achat', label: 'Achat', icon: ShoppingCart, color: '#F97316' },
  { id: 'location', label: 'Location', icon: Clock, color: '#3B82F6' },
  { id: 'echange', label: 'Échange', icon: Repeat, color: '#8B5CF6' },
  { id: 'don', label: 'Don', icon: HandHeart, color: '#22C55E' },
];

// États produit
const ETATS_PRODUIT = [
  { id: 'all', label: 'Tous états' },
  { id: 'neuf', label: 'Neuf' },
  { id: 'tres-bon', label: 'Très bon état' },
  { id: 'bon', label: 'Bon état' },
  { id: 'correct', label: 'Correct' },
  { id: 'a-renover', label: 'À rénover' },
];

// 16 Rayons avec icônes
const RAYONS_ICONS = {
  'rayon-electricite': Zap,
  'rayon-plomberie': Droplets,
  'rayon-jardin-paysage': TreeDeciduous,
  'rayon-piscine-spa': Droplets,
  'rayon-peinture-decoration': Paintbrush,
  'rayon-maconnerie-gros-oeuvre': HardHat,
  'rayon-renovation-interieure': Hammer,
  'rayon-carrelage-revetement': Layers,
  'rayon-menuiserie': Ruler,
  'rayon-chauffage': Flame,
  'rayon-couverture-toiture': Building2,
  'rayon-isolation': ShieldCheck,
  'rayon-climatisation-ventilation': Wind,
  'rayon-facade-enduit': Building2,
  'rayon-energie-solaire': Zap,
  'rayon-outillage-equipement': Wrench,
};

const RAYONS_COLORS = {
  'rayon-electricite': '#F59E0B',
  'rayon-plomberie': '#3B82F6',
  'rayon-jardin-paysage': '#22C55E',
  'rayon-piscine-spa': '#06B6D4',
  'rayon-peinture-decoration': '#EC4899',
  'rayon-maconnerie-gros-oeuvre': '#EA580C',
  'rayon-renovation-interieure': '#6366F1',
  'rayon-carrelage-revetement': '#10B981',
  'rayon-menuiserie': '#D97706',
  'rayon-chauffage': '#EF4444',
  'rayon-couverture-toiture': '#8B5CF6',
  'rayon-isolation': '#22C55E',
  'rayon-climatisation-ventilation': '#06B6D4',
  'rayon-facade-enduit': '#A855F7',
  'rayon-energie-solaire': '#F59E0B',
  'rayon-outillage-equipement': '#64748B',
};

// ══════════════════════════════════════════════════════════════════════════════
// CARTE PRODUIT UNIFIÉE - Pro vs Particulier
// ══════════════════════════════════════════════════════════════════════════════

function ProductCard({ product, index, onAddToCart, onToggleFavorite, isFavorite }) {
  const navigate = useNavigate();
  const { requireAuth } = useDemoMode();
  const isPro = product.seller_type === 'pro';

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (requireAuth('ajouter ce produit au panier')) {
      onAddToCart(product);
    }
  };

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    if (requireAuth('ajouter aux favoris')) {
      onToggleFavorite(product);
    }
  };

  const handleContact = (e) => {
    e.stopPropagation();
    if (requireAuth('contacter ce vendeur')) {
      navigate(`/produits/${product.id}`);
    }
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return 'Gratuit';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const getTransactionBadge = () => {
    const type = TRANSACTION_TYPES.find(t => t.id === product.transaction_type);
    if (!type || type.id === 'achat') return null;
    const Icon = type.icon;
    return (
      <div 
        className="absolute bottom-2 left-2 px-2 py-1 rounded-full text-[10px] font-bold text-white flex items-center gap-1"
        style={{ backgroundColor: type.color }}
      >
        <Icon size={10} />
        {type.label}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, delay: index * 0.02 }}
      className={`bg-white rounded-lg overflow-hidden border relative group hover:shadow-lg transition-shadow flex flex-col h-full ${
        isPro ? 'border-gray-200' : 'border-orange-200'
      }`}
      data-testid={`product-card-${product.id}`}
    >
      {/* Badge Pro Certifié / Particulier Certifié */}
      <div 
        className={`absolute top-2 left-2 z-10 px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 ${
          isPro 
            ? 'bg-[#1E3A5F] text-white' 
            : 'bg-orange-100 text-orange-700 border border-orange-300'
        }`}
      >
        {isPro ? (
          <>
            <BadgeCheck size={12} />
            Pro Certifié
          </>
        ) : (
          <>
            <User size={12} />
            Particulier
          </>
        )}
      </div>

      {/* Bouton Favoris */}
      <button
        onClick={handleToggleFavorite}
        className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/90 shadow-sm flex items-center justify-center hover:bg-white transition-colors"
        data-testid={`favorite-btn-${product.id}`}
      >
        <Heart 
          size={16} 
          className={isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-400'} 
        />
      </button>
      
      {/* Image - Format carré uniforme */}
      <div 
        className="relative aspect-square bg-gray-50 cursor-pointer overflow-hidden"
        onClick={() => navigate(`/produits/${product.id}`)}
      >
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <Package size={40} className="text-gray-300" />
          </div>
        )}
        
        {/* Badge Type Transaction (Location, Échange, Don) */}
        {getTransactionBadge()}
        
        {/* Badge Livraison Gratuite - Pro seulement */}
        {isPro && product.delivery_free && (
          <div className="absolute bottom-2 right-2 bg-green-600 text-white px-2 py-0.5 text-[10px] font-bold rounded-sm flex items-center gap-1">
            <Truck size={10} />
            Gratuite
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col flex-1">
        {/* Rayon badge */}
        {product.rayon_name && (
          <span 
            className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full mb-1 w-fit"
            style={{ 
              backgroundColor: `${RAYONS_COLORS[product.rayon_id] || '#6B7280'}15`,
              color: RAYONS_COLORS[product.rayon_id] || '#6B7280'
            }}
          >
            {product.rayon_name}
          </span>
        )}
        
        <h3 
          className="text-sm text-gray-900 line-clamp-2 leading-tight mb-1 cursor-pointer hover:text-[#F97316] min-h-[40px]"
          onClick={() => navigate(`/produits/${product.id}`)}
        >
          {product.name}
        </h3>

        {/* Marque (Pro) ou Localisation (Particulier) */}
        {isPro ? (
          product.brand && (
            <p className="text-xs text-gray-500 mb-1">{product.brand}</p>
          )
        ) : (
          product.ville && (
            <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <MapPin size={10} />
              {product.ville}
            </p>
          )
        )}

        {/* Note - Pro seulement */}
        {isPro && product.rating > 0 && (
          <div className="flex items-center gap-1 mb-1">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={12}
                  className={i < Math.floor(product.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
                />
              ))}
            </div>
            <span className="text-[10px] text-blue-600">({product.reviews_count || 0})</span>
          </div>
        )}

        {/* État (Particulier principalement) */}
        {!isPro && product.etat && product.etat !== 'neuf' && (
          <span className="text-[10px] text-gray-500 mb-1">
            État : {ETATS_PRODUIT.find(e => e.id === product.etat)?.label || product.etat}
          </span>
        )}

        {/* Prix */}
        <div className="flex items-baseline gap-2 mb-2 min-h-[28px]">
          <span className={`text-lg font-bold ${product.price === 0 ? 'text-[#22C55E]' : 'text-[#F97316]'}`}>
            {formatPrice(product.price)}
          </span>
          {product.negociable && (
            <span className="text-xs text-gray-500">(Négociable)</span>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Stock (Pro) */}
        {isPro && (
          <p className={`text-xs mb-2 ${product.in_stock ? 'text-green-600' : 'text-red-500'}`}>
            {product.stock_status || (product.in_stock ? 'En stock' : 'Rupture de stock')}
          </p>
        )}
        
        {/* Actions - Différentes selon Pro/Particulier */}
        {isPro ? (
          <button
            onClick={handleAddToCart}
            disabled={!product.in_stock}
            className="w-full py-2 px-4 bg-white hover:bg-[#F97316] active:bg-[#E8650A] border-2 border-[#F97316] rounded-lg text-sm font-semibold text-[#F97316] hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`add-to-cart-${product.id}`}
          >
            Ajouter au panier
          </button>
        ) : (
          <div className="flex gap-2">
            <Link 
              to={`/produits/${product.id}`}
              className="flex-1 py-2 px-3 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:border-orange-500 hover:text-orange-600 flex items-center justify-center gap-1"
            >
              <Eye size={14} />
              Voir
            </Link>
            <button 
              onClick={handleContact}
              className="flex-1 py-2 px-3 bg-[#F97316] text-white rounded-lg text-sm font-medium hover:bg-[#EA580C] flex items-center justify-center gap-1"
            >
              <MessageCircle size={14} />
              Contacter
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PILLS FILTRES - Tout | Pro | Particulier
// ══════════════════════════════════════════════════════════════════════════════

function SellerTypePills({ value, onChange }) {
  return (
    <div className="inline-flex items-center gap-1 p-1 rounded-full bg-[#f0f0f0]">
      {SELLER_TYPE_FILTERS.map((filter) => {
        const Icon = filter.icon;
        const isActive = value === filter.id;
        return (
          <button
            key={filter.id}
            onClick={() => onChange(filter.id)}
            className="flex items-center gap-1 rounded-full whitespace-nowrap transition-all duration-200"
            style={{
              fontSize: '12px',
              padding: '4px 12px',
              fontWeight: 600,
              backgroundColor: isActive ? '#E8650A' : '#f5f5f5',
              color: isActive ? '#ffffff' : '#555555',
            }}
            data-testid={`seller-type-${filter.id}`}
          >
            <Icon size={12} />
            <span>{filter.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PILLS TRANSACTION TYPE - Achat | Location | Échange | Don
// ══════════════════════════════════════════════════════════════════════════════

function TransactionTypePills({ value, onChange }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
      <button
        onClick={() => onChange(null)}
        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
          !value 
            ? 'bg-gray-800 text-white' 
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
      >
        Tout
      </button>
      {TRANSACTION_TYPES.map(type => {
        const Icon = type.icon;
        const isActive = value === type.id;
        return (
          <button
            key={type.id}
            onClick={() => onChange(type.id)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
              isActive 
                ? 'text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            style={isActive ? { backgroundColor: type.color } : {}}
          >
            <Icon size={12} />
            {type.label}
          </button>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE MARKETPLACE UNIFIÉE
// ══════════════════════════════════════════════════════════════════════════════

export default function MarketplaceUnifiePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Détecter la sous-page
  const isRayonsPage = location.pathname.includes('/rayons');
  const isVentesFlashPage = location.pathname.includes('/ventes-flash');
  const isMeilleuresVentesPage = location.pathname.includes('/meilleures-ventes');
  const isNouveautesPage = location.pathname.includes('/nouveautes');
  
  // États principaux
  const [products, setProducts] = useState([]);
  const [rayons, setRayons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [activeSubPage, setActiveSubPage] = useState(
    isRayonsPage ? 'rayons' : 
    isVentesFlashPage ? 'ventes-flash' : 
    isMeilleuresVentesPage ? 'meilleures-ventes' : 
    isNouveautesPage ? 'nouveautes' : 'tous'
  );

  // Filtres - FORCÉ PRO
  const [filters, setFilters] = useState({
    search: searchParams.get('q') || '',
    rayon_id: searchParams.get('rayon') || null,
    sous_rayon_id: searchParams.get('sous_rayon') || null,
    category_id: searchParams.get('category') || null,
    brand: searchParams.get('brand') || null,
    seller_type: 'pro', // FORCÉ PRO
    transaction_type: searchParams.get('transaction') || null,
    etat: searchParams.get('etat') || 'all',
    price: searchParams.get('price') || 'all',
    min_price: searchParams.get('min_price') ? parseFloat(searchParams.get('min_price')) : null,
    max_price: searchParams.get('max_price') ? parseFloat(searchParams.get('max_price')) : null,
    min_rating: searchParams.get('rating') ? parseInt(searchParams.get('rating')) : null,
    free_shipping: searchParams.get('free_shipping') === 'true',
    in_stock: searchParams.get('in_stock') === 'true',
    sort: searchParams.get('sort') || 'relevance'
  });

  // États des panels
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showCatalogueMenu, setShowCatalogueMenu] = useState(false);

  // Contexte acheteur
  const { 
    cart, favorites, cartCount, favoritesCount,
    addToCart: contextAddToCart, 
    toggleFavorite: contextToggleFavorite, 
    isFavorite: contextIsFavorite,
  } = useBuyer();

  // Charger les rayons au démarrage
  useEffect(() => {
    fetchRayons();
  }, []);

  // Charger les produits quand les filtres changent
  useEffect(() => {
    fetchProducts();
    updateSearchParams();
  }, [filters]);

  const fetchRayons = async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketplace/rayons`);
      if (res.ok) {
        const data = await res.json();
        setRayons(data);
      }
    } catch (e) {
      console.error('Error fetching rayons:', e);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.set('search', filters.search);
      if (filters.rayon_id) params.set('rayon_id', filters.rayon_id);
      if (filters.sous_rayon_id) params.set('sous_rayon_id', filters.sous_rayon_id);
      if (filters.category_id) params.set('category_id', filters.category_id);
      if (filters.brand) params.set('brand', filters.brand);
      // FORCÉ PRO
      params.set('seller_type', 'pro');
      if (filters.transaction_type) params.set('transaction_type', filters.transaction_type);
      if (filters.etat && filters.etat !== 'all') params.set('etat', filters.etat);
      if (filters.min_price !== null) params.set('min_price', filters.min_price.toString());
      if (filters.max_price !== null) params.set('max_price', filters.max_price.toString());
      if (filters.min_rating) params.set('min_rating', filters.min_rating.toString());
      if (filters.free_shipping) params.set('free_shipping', 'true');
      if (filters.in_stock) params.set('in_stock', 'true');
      if (filters.sort) params.set('sort', filters.sort);
      params.set('per_page', '48');

      const res = await fetch(`${API_URL}/api/marketplace/products?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
        setTotalProducts(data.total || 0);
      } else {
        setProducts([]);
        setTotalProducts(0);
      }
    } catch (e) {
      console.error('Error fetching products:', e);
      setProducts([]);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };

  const updateSearchParams = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set('q', filters.search);
    if (filters.rayon_id) params.set('rayon', filters.rayon_id);
    if (filters.sous_rayon_id) params.set('sous_rayon', filters.sous_rayon_id);
    if (filters.category_id) params.set('category', filters.category_id);
    if (filters.brand) params.set('brand', filters.brand);
    if (filters.seller_type !== 'all') params.set('seller_type', filters.seller_type);
    if (filters.transaction_type) params.set('transaction', filters.transaction_type);
    if (filters.etat !== 'all') params.set('etat', filters.etat);
    if (filters.price !== 'all') params.set('price', filters.price);
    if (filters.min_rating) params.set('rating', filters.min_rating.toString());
    if (filters.free_shipping) params.set('free_shipping', 'true');
    if (filters.in_stock) params.set('in_stock', 'true');
    if (filters.sort !== 'relevance') params.set('sort', filters.sort);
    setSearchParams(params, { replace: true });
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      rayon_id: null,
      sous_rayon_id: null,
      category_id: null,
      brand: null,
      seller_type: 'pro', // TOUJOURS PRO
      transaction_type: null,
      etat: 'all',
      price: 'all',
      min_price: null,
      max_price: null,
      min_rating: null,
      free_shipping: false,
      in_stock: false,
      sort: 'relevance'
    });
  };

  const handleSearch = ({ query, rayon_id, sous_rayon_id, category_id }) => {
    setFilters(prev => ({
      ...prev,
      search: query || '',
      rayon_id: rayon_id || prev.rayon_id,
      sous_rayon_id: sous_rayon_id || null,
      category_id: category_id || null,
    }));
  };

  const addToCart = (product) => {
    contextAddToCart({
      id: product.id,
      title: product.name,
      price: product.price,
      image: product.image,
      photos: product.image ? [product.image] : []
    }, 1);
  };

  const toggleFavorite = (product) => {
    contextToggleFavorite({
      id: product.id,
      title: product.name,
      price: product.price,
      image: product.image
    });
  };

  // Compteur filtres actifs
  const activeFiltersCount = [
    filters.rayon_id,
    filters.brand,
    filters.seller_type !== 'all',
    filters.transaction_type,
    filters.etat !== 'all',
    filters.price !== 'all',
    filters.min_rating,
    filters.free_shipping,
    filters.in_stock
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-['Inter',sans-serif]" data-testid="marketplace-unifie-page">
      {/* Header Unifié */}
      <UnifiedHeader 
        onSearch={handleSearch}
        cartCount={cartCount}
        showCart={true}
        marketplaceMode={true}
        initialSearchQuery={filters.search}
        onOpenFilters={() => setShowMobileFilters(true)}
      />
      
      {/* Navigation Principale */}
      <MainNavigation 
        onOpenRayonsMenu={() => setShowCatalogueMenu(true)} 
        onOpenFilters={() => setShowMobileFilters(true)}
      />

      {/* Spacer Mobile */}
      <div className="h-11 md:hidden" />

      {/* Spacer Desktop/Tablet */}
      <div className="hidden md:block" style={{ height: '100px' }} />

      {/* ═══ HERO BANNER MARKETPLACE UNIFIÉE — Mobile uniquement ═══ */}
      <div className="lg:hidden bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)'
          }} />
        </div>
        
        {/* Version MOBILE - Bannière Premium Apple/Stripe */}
        <div className="px-4 pt-6 pb-3">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white whitespace-nowrap leading-none">
              Trouve tes produits
            </h1>
            <p className="text-white/90 text-sm font-normal whitespace-nowrap leading-none mt-2">
              En <span className="font-semibold">3 clics</span>, sans <span className="font-semibold">intermédiaire</span> ni <span className="font-semibold">commission</span>
            </p>
          </div>
        </div>
      </div>

      {/* ═══ FILTRES PILLS - Supprimé car seller_type forcé PRO ═══ */}

      {/* Badges de confiance - Masqué sur Desktop */}

      {/* Filtres actifs */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 overflow-x-auto bg-gray-50" style={{ scrollbarWidth: 'none' }}>
          {filters.search && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">
              "{filters.search}"
              <button onClick={() => handleFilterChange({ search: '' })} className="hover:bg-gray-200 rounded p-0.5 ml-0.5">
                <X size={10} />
              </button>
            </span>
          )}
          {filters.rayon_id && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">
              {rayons.find(r => r.id === filters.rayon_id)?.name}
              <button onClick={() => handleFilterChange({ rayon_id: null, brand: null })} className="hover:bg-gray-200 rounded p-0.5 ml-0.5">
                <X size={10} />
              </button>
            </span>
          )}
          {filters.transaction_type && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">
              {TRANSACTION_TYPES.find(t => t.id === filters.transaction_type)?.label}
              <button onClick={() => handleFilterChange({ transaction_type: null })} className="hover:bg-gray-200 rounded p-0.5 ml-0.5">
                <X size={10} />
              </button>
            </span>
          )}
          {filters.etat !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">
              {ETATS_PRODUIT.find(e => e.id === filters.etat)?.label}
              <button onClick={() => handleFilterChange({ etat: 'all' })} className="hover:bg-gray-200 rounded p-0.5 ml-0.5">
                <X size={10} />
              </button>
            </span>
          )}
          <button
            onClick={handleResetFilters}
            className="text-xs text-gray-500 hover:text-red-600 font-medium whitespace-nowrap ml-1"
          >
            Tout effacer
          </button>
        </div>
      )}

      {/* Résultats Header - Compact sur mobile */}
      <div className="bg-white md:border-b md:border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-1 md:py-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-bold text-base md:text-lg text-gray-900">{totalProducts}</span>
              <span className="text-gray-600 ml-1 text-sm">résultat{totalProducts !== 1 ? 's' : ''}</span>
            </div>
            
            {/* Tri */}
            <select
              value={filters.sort}
              onChange={(e) => handleFilterChange({ sort: e.target.value })}
              className="text-xs md:text-sm border border-gray-200 rounded-lg px-2 py-1 md:px-3 md:py-1.5 bg-white focus:outline-none focus:border-[#F97316]"
            >
              <option value="relevance">Pertinence</option>
              <option value="price_asc">Prix ↑</option>
              <option value="price_desc">Prix ↓</option>
              <option value="newest">Récents</option>
              <option value="rating">Notes</option>
            </select>
          </div>
        </div>
      </div>

      {/* Contenu Principal - Sans marge sur mobile */}
      <div className="max-w-7xl mx-auto px-4 pb-2 md:py-6">
        {/* ═══ PAGE RAYONS - Grille des catégories ═══ */}
        {isRayonsPage && !filters.rayon_id && (
          <div className="py-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Tous les Rayons</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {Object.entries(RAYONS_ICONS).map(([rayonId, Icon]) => {
                const color = RAYONS_COLORS[rayonId] || '#6B7280';
                const name = rayonId.replace('rayon-', '').split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                return (
                  <Link
                    key={rayonId}
                    to={`/marketplace?rayon=${rayonId}`}
                    className="bg-white rounded-xl border border-gray-200 p-6 text-center hover:shadow-lg transition-all group"
                  >
                    <div 
                      className="w-16 h-16 mx-auto rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: `${color}15` }}
                    >
                      <Icon size={32} style={{ color }} />
                    </div>
                    <h3 className="font-semibold text-gray-900">{name}</h3>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ PAGE VENTES FLASH ═══ */}
        {isVentesFlashPage && (
          <div className="py-4">
            <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-6 mb-6 text-center text-white">
              <h2 className="text-2xl font-bold mb-2">🔥 Ventes Flash</h2>
              <p className="text-white/90">Les meilleures offres du moment - Jusqu'à -50%</p>
            </div>
          </div>
        )}

        {/* ═══ PAGE MEILLEURES VENTES ═══ */}
        {isMeilleuresVentesPage && (
          <div className="py-4">
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl p-6 mb-6 text-center text-white">
              <h2 className="text-2xl font-bold mb-2">⭐ Meilleures Ventes</h2>
              <p className="text-white/90">Les produits les plus populaires</p>
            </div>
          </div>
        )}

        {/* ═══ PAGE NOUVEAUTÉS ═══ */}
        {isNouveautesPage && (
          <div className="py-4">
            <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-xl p-6 mb-6 text-center text-white">
              <h2 className="text-2xl font-bold mb-2">✨ Nouveautés</h2>
              <p className="text-white/90">Les derniers produits ajoutés</p>
            </div>
          </div>
        )}

        {/* ═══ PRODUITS GRID ═══ */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <ProductSkeleton />
              </motion.div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Package size={56} className="mx-auto text-gray-300 mb-4" />
            <h3 className="font-bold text-xl text-gray-900 mb-2">Aucun produit trouvé</h3>
            <p className="text-gray-500 mb-6">Essaie de modifier tes critères de recherche</p>
            <button 
              onClick={handleResetFilters} 
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E3A5F] text-white rounded-xl font-medium hover:bg-[#2D4A6F] transition-colors"
            >
              <RefreshCw size={18} />
              Voir tous les produits
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {products.map((product, index) => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  index={index}
                  onAddToCart={addToCart}
                  onToggleFavorite={toggleFavorite}
                  isFavorite={contextIsFavorite(product.id)}
                />
              ))}
            </div>
          )}
      </div>

      {/* Trust Footer */}
      <div className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {[
              { icon: Truck, text: 'Livraison rapide', subtext: 'Partout en France' },
              { icon: Shield, text: 'Paiement sécurisé', subtext: '100% protégé' },
              { icon: Tag, text: 'Meilleurs prix', subtext: 'Sans commission' },
              { icon: Star, text: 'Vendeurs certifiés', subtext: 'Pro & Particuliers' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-xl bg-[#1E3A5F]/10 flex items-center justify-center mb-3">
                  <item.icon size={24} className="text-[#1E3A5F]" />
                </div>
                <span className="font-semibold text-gray-900">{item.text}</span>
                <span className="text-sm text-gray-500">{item.subtext}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Système de Filtres Unifié */}
      <UnifiedFilterSystem
        context="marketplace"
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        filters={filters}
        onFiltersChange={handleFilterChange}
        resultsCount={totalProducts}
        onApply={() => setShowMobileFilters(false)}
        rayons={rayons}
        sousRayons={[]}
        marques={[]}
      />
      
      {/* Menu Catalogue */}
      <MarketplaceCatalogueMenu 
        isOpen={showCatalogueMenu}
        onClose={() => setShowCatalogueMenu(false)}
      />
      
      {/* Footer Global */}
      
      
      {/* Spacer mobile pour navigation */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}
